import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Save, X, Send, TestTube, Link2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './input';
import { AIEmail } from '@prisma/client';
import Link from 'next/link';
import { StrategyAIEmail } from 'types/extend-prisma';

interface AIEmailSuggestionProps {
	email: AIEmail | StrategyAIEmail;
	onSend?: (email: AIEmail | StrategyAIEmail) => Promise<void>;
	onDiscard?: (email: AIEmail | StrategyAIEmail) => Promise<void>;
	setEmail: (email: AIEmail | StrategyAIEmail) => void;
	isNewDisclaimer?: boolean;
	disabled?: boolean;
	onTestEmail?: (email: AIEmail | StrategyAIEmail) => void;
}
const AIEmailSuggestion: React.FC<AIEmailSuggestionProps> = ({
	email,
	onSend,
	onDiscard,
	disabled,
	setEmail,
	onTestEmail
}) => {
	const [isFocused, setIsFocused] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [initialEmail, setInitialEmail] = useState(JSON.parse(JSON.stringify(email)));

	const isEmailEmpty = useMemo(() => {
		return !email.subject?.trim() && !email.body?.trim();
	}, [email.subject, email.body]);

	const handleChange = (field: keyof AIEmail, value: string) => {
		email = { ...email, [field]: value };
		setEmail(email);
		setIsEditing(true);
	};

	const handleFocus = () => {
		setIsFocused(true);
		setIsEditing(true);
	};

	const handleBlur = () => {
		setIsFocused(false);
	};

	const handleUndo = () => {
		setIsLoading(true);
		email.body = initialEmail.body;
		email.subject = initialEmail.subject;
		setEmail(email);
		setIsEditing(false);
		setIsLoading(false);
	};

	// Relevant for new disclaimers
	const handleAdd = () => {
		if (isEmailEmpty) return;
		setIsLoading(true);
		onSend?.(email).then(() => {
			setIsEditing(false);
			setIsLoading(false);
			setInitialEmail(JSON.parse(JSON.stringify(email)));
		});
	};

	const handleDiscard = () => {
		setIsLoading(true);
		onDiscard?.(email).then(() => {
			setIsEditing(false);
			setIsLoading(false);
		});
	};

	return (
		<Card className={cn('w-full mb-6 transition-all duration-300', isFocused && 'shadow-xl')}>
			<CardHeader className="pb-4">
				<CardTitle>To {email.email}</CardTitle>
				<div className="flex items-center space-x-2">
					<Link2Icon className="h-4 w-4" />
					<Link
						className="text-black-500 underline"
						href={`https://${email.email.split('@').pop()}`}
						target="_blank"
					>
						{email.email.split('@').pop()}
					</Link>
				</div>
				{email.company_title && email.company_description && (
					<div className="p-2 text-sm border-none rounded-md bg-muted">
						{email.company_title && (
							<h4 className="font-semibold">{email.company_title}</h4>
						)}
						{email.company_description && <p>{email.company_description}</p>}
						{email.summary && (
							<p>
								<strong>About:{` `}</strong>
								{email.summary}
							</p>
						)}
					</div>
				)}
			</CardHeader>
			<CardContent>
				<div className="grid gap-4 mt-2">
					<Input
						value={email.subject ?? ''}
						onChange={(e: { target: { value: string } }) => {
							handleChange('subject', e.target.value);
						}}
						onFocus={handleFocus}
						onBlur={handleBlur}
						className="col-span-3"
						id="email-subject"
						placeholder="Email subject"
						disabled={isLoading}
					/>
					<Textarea
						value={email.body ?? ''}
						onChange={(e: { target: { value: string } }) =>
							handleChange('body', e.target.value)
						}
						placeholder="Message body"
						id="email-content"
						disabled={isLoading}
						onFocus={handleFocus}
						onBlur={handleBlur}
						rows={10}
						className="col-span-3"
					/>
				</div>
				<div className="flex justify-end mt-4 gap-1 flex-wrap">
					{/* Resting State */}
					{!isEditing && !isEmailEmpty && (
						<>
							<Button
								variant="outline"
								size="sm"
								onClick={handleAdd}
								disabled={isLoading || disabled}
								className="text-green-700 hover:bg-green-700 hover:text-white"
							>
								<Send className="h-4 w-4 mr-2" />
								Send
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleDiscard}
								disabled={isLoading}
							>
								<X className="h-4 w-4 mr-2" />
								Discard
							</Button>
						</>
					)}
					{!isEditing && isEmailEmpty && (
						<>
							<Button
								variant="outline"
								size="sm"
								disabled
								className="text-green-700 hover:bg-green-700 hover:text-white"
							>
								<Send className="h-4 w-4 mr-2" />
								Send
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleDiscard}
								disabled={isLoading}
							>
								<X className="h-4 w-4 mr-2" />
								Discard
							</Button>
						</>
					)}
					{/* Active State */}
					{isEditing && !isEmailEmpty && (
						<>
							<Button
								variant="outline"
								size="sm"
								onClick={handleAdd}
								disabled={isLoading || disabled}
								className="text-green-700 hover:bg-green-700 hover:text-white"
							>
								<Send className="h-4 w-4 mr-2" />
								Send
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleUndo}
								disabled={isLoading}
							>
								<X className="h-4 w-4 mr-2" />
								Undo
							</Button>
						</>
					)}
					{isEditing && isEmailEmpty && (
						<>
							<Button
								variant="outline"
								size="sm"
								disabled
								className="text-green-700 hover:bg-green-700 hover:text-white"
							>
								<Save className="h-4 w-4 mr-2" />
								Save
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleUndo}
								disabled={isLoading}
							>
								<X className="h-4 w-4 mr-2" />
								Undo
							</Button>
						</>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => onTestEmail?.(email)}
						disabled={isLoading || disabled}
						className="text-blue-700 hover:bg-blue-700 hover:text-white hidden md:flex"
					>
						<TestTube className="h-4 w-4 mr-2" />
						Test Email
					</Button>
				</div>
			</CardContent>
		</Card>
	);
};

export default AIEmailSuggestion;
